import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { jornaisApi, type Etapa } from '@/services/jornais';

import estilos from './ExemplaresDaLauda.module.css';

/**
 * Os EXEMPLARES de uma lauda — o mesmo fato, nas formas em que ele saiu.
 *
 * É o estado 2 do quadro: da manchete se abre o que aquela matéria virou em
 * cada lugar. A redação de TV não pensa em "etapa 11 da esteira"; ela pensa
 * "isso já está no site? já subiu no YouTube? o vertical saiu?". Esta tela
 * responde nessa ordem, e a esteira inteira continua a um clique.
 *
 * REGRA DESTA TELA: exemplar que não existe aparece **como não existente**, com
 * o motivo. Esconder a linha faz o editor achar que ninguém pediu; mostrar
 * "pendente" quando a etapa foi PULADA faz ele esperar por algo que não vem.
 */

/** As formas em que o fato sai, na ordem em que a redação pergunta. */
const EXEMPLARES: { etapa: string; rotulo: string; onde: string }[] = [
  { etapa: 'publicacao_site', rotulo: 'Matéria no portal', onde: 'site' },
  { etapa: 'video_integral_16x9', rotulo: 'Íntegra 16:9', onde: 'arquivo' },
  { etapa: 'video_highlight_16x9', rotulo: 'Melhor trecho 16:9', onde: 'arquivo' },
  { etapa: 'video_integral_9x16', rotulo: 'Íntegra vertical', onde: 'arquivo' },
  { etapa: 'video_short_9x16', rotulo: 'Shorts 9:16', onde: 'arquivo' },
  { etapa: 'publicacao_youtube', rotulo: 'YouTube', onde: 'rede' },
  { etapa: 'publicacao_redes', rotulo: 'Redes sociais', onde: 'rede' },
];

/** O que o estado da etapa quer dizer para quem só quer saber se saiu. */
const EM_PALAVRAS: Record<string, string> = {
  pronto: 'no ar',
  rodando: 'saindo agora',
  pendente: 'ainda não',
  falhou: 'falhou',
  pulada: 'não se aplica',
  manual: 'no ar (à mão)',
};

/** O endereço público do exemplar, se a etapa tiver deixado um. */
function enderecoDe(e: Etapa | undefined): string | null {
  const s = e?.saida;
  if (!s) return null;
  const direto = s.wp_link || s.url || s.link;
  if (typeof direto === 'string' && direto.startsWith('http')) return direto;
  // As etapas de rede guardam um mapa por plataforma: `publicados[nome].url`.
  const publicados = s.publicados as Record<string, { url?: string }> | undefined;
  if (publicados) {
    for (const v of Object.values(publicados)) {
      if (v && typeof v.url === 'string' && v.url.startsWith('http')) return v.url;
    }
  }
  return null;
}

/** As plataformas em que aquele exemplar de rede realmente saiu, com o veredito. */
function porPlataforma(
  e: Etapa | undefined,
): { nome: string; ok: boolean; url?: string; erro?: string }[] {
  const publicados = e?.saida?.publicados as
    | Record<string, { ok?: boolean; url?: string; error?: string }>
    | undefined;
  if (!publicados) return [];
  return Object.entries(publicados).map(([nome, v]) => ({
    nome,
    ok: Boolean(v?.ok),
    url: typeof v?.url === 'string' ? v.url : undefined,
    // O motivo da recusa vem junto: "x" em vermelho sozinho nao diz se e
    // credencial vencida, teto de cota ou rede fora — e sao acoes diferentes.
    erro: typeof v?.error === 'string' ? v.error : undefined,
  }));
}

/**
 * O que o operador FAZ com a matéria, pelo card (ADM, 11/09/2026): editar o
 * artigo e subir foto ou vídeo. Foto vira a imagem destacada; vídeo entra no
 * topo do corpo. O post continua no status em que está — rascunho não vira
 * publicado por ter ganhado mídia.
 */
function AcoesDaMateria({
  laudaId,
  wpId,
  site,
}: {
  laudaId: number;
  wpId: number | null | undefined;
  site: string | null | undefined;
}) {
  const qc = useQueryClient();
  const [aviso, setAviso] = useState<string | null>(null);
  const subir = useMutation({
    mutationFn: (arquivo: File) => jornaisApi.subirMidia(laudaId, arquivo),
    onSuccess: (r) => {
      setAviso(
        r.papel === 'foto'
          ? 'Foto enviada: virou a imagem destacada da matéria.'
          : 'Vídeo enviado: entrou no topo do corpo da matéria.',
      );
      void qc.invalidateQueries({ queryKey: ['jornais', 'esteira', laudaId] });
    },
  });

  if (!wpId) {
    return (
      <p className={estilos.fraco}>
        A matéria ainda não foi escrita: editar e subir mídia ficam disponíveis quando o
        rascunho existir no WordPress.
      </p>
    );
  }

  return (
    <section className={estilos.item} aria-label="Ações da matéria">
      <div className={estilos.linha}>
        <Link
          to={`/artigos/${wpId}${site ? `?site=${encodeURIComponent(site)}` : ''}`}
          className={estilos.paraEsteira}
        >
          Editar o artigo →
        </Link>
      </div>
      <label className={estilos.fraco}>
        Subir mídia para a matéria — foto ou vídeo, até 50 MB:{' '}
        <input
          type="file"
          accept="image/*,video/*"
          disabled={subir.isPending}
          onChange={(ev) => {
            const arquivo = ev.target.files?.[0];
            if (arquivo) {
              setAviso(null);
              subir.mutate(arquivo);
            }
            ev.target.value = '';
          }}
        />
      </label>
      {subir.isPending && <p className={estilos.fraco}>Enviando…</p>}
      {aviso && (
        <p className={estilos.fraco} role="status">
          {aviso}
        </p>
      )}
      {subir.isError && (
        <p className={estilos.erro} role="alert">
          {(subir.error as Error).message}
        </p>
      )}
    </section>
  );
}

export function ExemplaresDaLauda({
  laudaId,
  titulo,
  aoFechar,
}: {
  laudaId: number;
  titulo: string;
  aoFechar: () => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['jornais', 'esteira', laudaId],
    queryFn: () => jornaisApi.esteira(laudaId),
  });

  const caixa = useRef<HTMLDivElement>(null);

  // Esc fecha. Um modal que só fecha no X prende quem navega por teclado.
  useEffect(() => {
    const noTeclado = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') aoFechar();
    };
    document.addEventListener('keydown', noTeclado);
    caixa.current?.focus();
    return () => document.removeEventListener('keydown', noTeclado);
  }, [aoFechar]);

  const porEtapa = new Map((data?.etapas ?? []).map((e) => [e.etapa, e]));
  // A etapa grava `{title}` (content_engine); `headline` fica como fallback para
  // livro antigo. Lendo so `headline`, o modal caia no titulo do espelho.
  const saidaManchete = porEtapa.get('manchete')?.saida;
  const manchete = (saidaManchete?.title ?? saidaManchete?.headline) as string | undefined;

  return (
    <div className={estilos.fundo} onClick={aoFechar} role="presentation">
      <div
        className={estilos.caixa}
        role="dialog"
        aria-modal="true"
        aria-label={`Exemplares de: ${titulo || 'lauda sem título'}`}
        tabIndex={-1}
        ref={caixa}
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className={estilos.cabecalho}>
          <div>
            <p className={estilos.olho}>Exemplares desta lauda</p>
            <h2 className={estilos.manchete}>{manchete || titulo || 'sem título'}</h2>
            {manchete && manchete !== titulo && titulo && (
              <p className={estilos.fraco}>
                título da lauda no espelho: <span className={estilos.mono}>{titulo}</span>
              </p>
            )}
          </div>
          <button type="button" className={estilos.fechar} onClick={aoFechar} aria-label="Fechar">
            ×
          </button>
        </header>

        {isLoading && <p className={estilos.fraco}>Lendo a esteira desta lauda…</p>}
        {isError && (
          <p className={estilos.erro} role="alert">
            Não deu para ler a esteira. Isso é falha de leitura, não ausência de exemplar — a
            diferença importa antes de alguém concluir que a matéria não saiu.
          </p>
        )}

        {data && <AcoesDaMateria laudaId={laudaId} wpId={data.wp_id} site={data.site} />}

        {data && (
          <ul className={estilos.lista}>
            {EXEMPLARES.map(({ etapa, rotulo, onde }) => {
              const e = porEtapa.get(etapa);
              const estado = e?.estado ?? 'pendente';
              const endereco = enderecoDe(e);
              const plataformas = porPlataforma(e);
              return (
                <li key={etapa} className={estilos.item} data-onde={onde}>
                  <div className={estilos.linha}>
                    <span className={estilos.rotulo}>{rotulo}</span>
                    <span className={`${estilos.selo} ${estilos[estado] ?? ''}`}>
                      {EM_PALAVRAS[estado] ?? estado}
                    </span>
                  </div>

                  {endereco && (
                    <a
                      className={estilos.endereco}
                      href={endereco}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {endereco}
                    </a>
                  )}

                  {plataformas.length > 0 && (
                    <ul className={estilos.plataformas}>
                      {plataformas.map((p) => (
                        <li
                          key={p.nome}
                          className={p.ok ? estilos.pOk : estilos.pRuim}
                          title={p.erro}
                        >
                          {p.nome}
                          {p.erro && `: ${p.erro}`}
                          {p.url && (
                            <>
                              {' · '}
                              <a href={p.url} target="_blank" rel="noopener noreferrer">
                                abrir
                              </a>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* O motivo de NÃO ter saído vale tanto quanto o endereço de
                      quem saiu: é ele que diz se é para esperar ou para agir. */}
                  {e?.erro && <p className={estilos.erroItem}>{e.erro}</p>}
                  {estado === 'pulada' && !e?.erro && (
                    <p className={estilos.fraco}>
                      {(e?.saida?.motivo as string) ?? 'não se aplica a esta lauda'}
                    </p>
                  )}
                  {estado === 'pendente' && !e && (
                    <p className={estilos.fraco}>a esteira ainda não chegou aqui</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <footer className={estilos.rodape}>
          <Link to={`/jornais/${laudaId}`} className={estilos.paraEsteira}>
            Ver a matéria completa e as 13 etapas →
          </Link>
        </footer>
      </div>
    </div>
  );
}
